import React, { useEffect, useRef, useState } from "react";
import { regionalCountries, regionColors } from "@/config/regionalConfig";

const countries = Object.values(regionalCountries);

export default function RegionalGlobe() {
  const rootRef = useRef(null);
  const canvasRef = useRef(null);
  const globeRef = useRef(null);
  const [activeCountry, setActiveCountry] = useState(regionalCountries.ecuador);
  const [shouldInit, setShouldInit] = useState(false);
  const [globeUnavailable, setGlobeUnavailable] = useState(false);
  const selectedCountryRef = useRef("ecuador");

  useEffect(() => {
    const root = rootRef.current;
    if (!root || shouldInit) return undefined;

    const prepareGlobe = () => setShouldInit(true);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        prepareGlobe();
        observer.disconnect();
      },
      { rootMargin: "700px 0px 420px 0px", threshold: 0.01 }
    );

    observer.observe(root);

    return () => observer.disconnect();
  }, [shouldInit]);

  useEffect(() => {
    const root = rootRef.current;
    const canvas = canvasRef.current;
    if (!shouldInit || !root || !canvas) return undefined;

    let cancelled = false;
    // Load the 3D engine only near this section; country content works without WebGL.
    import("./earth-globe").then(({ createCaptecEarthGlobe }) => {
      if (cancelled) return;
      globeRef.current = createCaptecEarthGlobe({ root, canvas, onCountryChange: setActiveCountry });
      globeRef.current?.selectCountry(selectedCountryRef.current);
    }).catch(() => {
      if (!cancelled) setGlobeUnavailable(true);
    });

    return () => {
      cancelled = true;
      globeRef.current?.destroy?.();
      globeRef.current = null;
    };
  }, [shouldInit]);

  const selectCountry = (countryId) => {
    selectedCountryRef.current = countryId;
    setActiveCountry(regionalCountries[countryId]);
    setShouldInit(true);
    globeRef.current?.selectCountry(countryId);
  };

  return (
    <div className="regional-globe-shell" data-regional-globe ref={rootRef}
      style={{ "--region-active": regionColors.active, "--region-presence": regionColors.regional }}>
      <div className="regional-copy">
        <span className="regional-kicker">Presencia regional</span>

        <h2>Conectamos Latinoamérica a través de una misma visión de pagos.</h2>

        <p>
          CAPTEC tiene presencia en Ecuador, Colombia, Panamá, México y Honduras, combinando operación activa, alianzas y desarrollo comercial para acompañar proyectos regionales.
        </p>

        <div className={`region-info-card ${activeCountry.pending ? "pending" : ""}`} aria-live="polite">
          <div className="region-status-row">
            <span className="region-status-dot" />
            <span>{activeCountry.status}</span>
          </div>

          <h3>{activeCountry.name}</h3>

          <p>{activeCountry.description}</p>


        </div>

        <div className="region-country-list" aria-label="Países regionales">
          {countries.map((country) => (
            <button
              key={country.id}
              className={`region-country-btn ${
                activeCountry.id === country.id ? "active" : ""
              } ${country.pending ? "pending" : ""}`}
              type="button"
              aria-pressed={activeCountry.id === country.id}
              onClick={() => selectCountry(country.id)}
            >
              {country.name}
            </button>
          ))}
        </div>
      </div>

      <div className="globe-stage">
        <div className="satellite-frame">
          <div className="globe-toolbar" aria-label="Controles del globo satelital">
            <button type="button" aria-label="Acercar" disabled={globeUnavailable} onClick={() => globeRef.current?.zoomIn?.()}>
              +
            </button>
            <button type="button" aria-label="Alejar" disabled={globeUnavailable} onClick={() => globeRef.current?.zoomOut?.()}>
              −
            </button>
            <button type="button" aria-label="Restablecer vista" disabled={globeUnavailable} onClick={() => globeRef.current?.reset?.()}>
              ◎
            </button>
          </div>

          <div className="globe-viewport">
            {globeUnavailable && <img className="earth-fallback" src="/assets/cosmos/planet-earth.png" alt="Presencia regional de CAPTEC; selecciona un país para consultar su información" />}
            <canvas
              ref={canvasRef}
              className="earth-canvas"
              hidden={globeUnavailable}
              aria-label="Globo satelital interactivo"
            />
            <div className="earth-loading">
              {shouldInit ? "Cargando vista satelital" : "Preparando vista satelital"}
            </div>
          </div>

          <div className="globe-legend">
            <span>
              <i className="legend-active" />Operación activa
            </span>
            <span>
              <i className="legend-pending" />Presencia regional
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
